import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getUserByEmail, 
  createUser, 
  updateUser as updateUserInFirebase, 
  deleteUser
} from '../services/userService';
import { signInWithGoogle, signOut as googleSignOut, onAuthStateChange } from '../services/googleAuthService';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { deepStripUndefined } from '../utils/dataSanitizer';
import { auth } from '../config/firebase';
import userDataService from '../services/userDataService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Initialize default admin if it doesn't exist (disabled)
        // await initializeDefaultAdmin();
        
        // Load users from Firebase
        const { getAllUsers: getUsers } = await import('../services/userService');
        const firebaseUsers = await getUsers();
        
        // Clean up duplicates on app load
        try {
          const { cleanupDuplicateUsers } = await import('../utils/cleanupDuplicates');
          const cleanupResult = await cleanupDuplicateUsers();
          console.log('Duplicate cleanup result:', cleanupResult);
          
          // Reload users after cleanup
          if (cleanupResult.deleted > 0) {
            const updatedUsers = await getUsers();
            setUsers(updatedUsers);
          } else {
            setUsers(firebaseUsers);
          }
        } catch (error) {
          console.error('Error during duplicate cleanup:', error);
          setUsers(firebaseUsers);
        }
        
        // Set up Firebase auth state listener
        const unsubscribe = onAuthStateChange(async (firebaseUser) => {
          if (firebaseUser) {
            // User is signed in with Google
            try {
              console.log('Firebase Auth state changed, user:', firebaseUser.email);
              const userData = await getUserByEmail(firebaseUser.email);
              if (userData) {
                console.log('✅ User found in Firestore:', userData.email);
                const userWithoutPassword = { ...userData };
                delete userWithoutPassword.password;
                
                // Migrate localStorage data to Firebase if needed
                await userDataService.migrateLocalStorageData(userData.id);
                
                setUser(userWithoutPassword);
                localStorage.setItem('bullbearly-user', JSON.stringify(userWithoutPassword));
              } else {
                console.log('❌ User not found in Firestore - signup is disabled');
                // Sign out the user since they don't exist in our system
                await firebaseSignOut(auth);
                setUser(null);
                localStorage.removeItem('bullbearly-user');
              }
            } catch (error) {
              console.error('❌ Error getting user data:', error);
            }
          } else {
            // User is signed out
            console.log('User signed out');
            setUser(null);
            localStorage.removeItem('bullbearly-user');
          }
          setLoading(false);
        });
        
        // Cleanup listener on unmount
        return () => unsubscribe();
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // Get user from Firebase
      const foundUser = await getUserByEmail(email);
      
      if (!foundUser) {
        throw new Error('User not found');
      }

      // For demo purposes, accept any password for existing users
      // In production, you'd hash and compare passwords
      const userData = { ...foundUser };
      delete userData.password; // Don't store password in state
      
      // Migrate localStorage data to Firebase if needed
      await userDataService.migrateLocalStorageData(userData.id);
      
      setUser(userData);
      localStorage.setItem('bullbearly-user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signup = async (userData) => {
    try {
      console.log('AuthContext signup called with:', userData);
      
      // Double-check if user already exists (additional protection)
      const existingUser = await getUserByEmail(userData.email);
      if (existingUser) {
        console.log('User already exists in AuthContext signup, returning existing user:', existingUser.email);
        const userWithoutPassword = { ...existingUser };
        delete userWithoutPassword.password;
        setUser(userWithoutPassword);
        localStorage.setItem('bullbearly-user', JSON.stringify(userWithoutPassword));
        return userWithoutPassword;
      }

      // Create new user in Firebase
      const newUserData = {
        fullName: userData.fullName,
        email: userData.email,
        phone: userData.phone,
        role: 'USER',
        subscriptionType: userData.subscriptionType || 'EARLY_BIRD',
        // In production, you'd hash the password
        password: userData.password || '',
        authProvider: userData.authProvider || 'email',
        googleUid: userData.googleUid || null,
        profilePicture: userData.profilePicture || '',
        // Add payment information if available
        paymentInfo: userData.paymentInfo || null,
        createdAt: new Date().toISOString()
      };

      // Sanitize data to remove undefined values before saving to Firestore
      const sanitizedUserData = deepStripUndefined(newUserData);
      
      console.log('Creating new user in Firebase:', sanitizedUserData);
      const newUser = await createUser(sanitizedUserData);
      
      // Update local users state
      setUsers(prev => [...prev, newUser]);
      
      // Auto-login after signup
      const userWithoutPassword = { ...newUser };
      delete userWithoutPassword.password;
      
      setUser(userWithoutPassword);
      localStorage.setItem('bullbearly-user', JSON.stringify(userWithoutPassword));
      return userWithoutPassword;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const userData = await signInWithGoogle();
      
      // For new users, we'll handle the signup flow differently
      if (userData.isNewUser) {
        console.log('New user from Google signup, returning user data for signup flow');
        return userData;
      }
      
      // Migrate localStorage data to Firebase if needed
      await userDataService.migrateLocalStorageData(userData.id);
      
      setUser(userData);
      localStorage.setItem('bullbearly-user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Sign out from Firebase Auth (handles both Google and regular logout)
      await googleSignOut();
      setUser(null);
      localStorage.removeItem('bullbearly-user');
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback to local logout
      setUser(null);
      localStorage.removeItem('bullbearly-user');
    }
  };

  const updateUser = async (updatedUserData) => {
    if (!user) return;
    
    try {
      // Update user in Firebase
      await updateUserInFirebase(user.id, updatedUserData);
      
      const updatedUser = { ...user, ...updatedUserData };
      setUser(updatedUser);
      localStorage.setItem('bullbearly-user', JSON.stringify(updatedUser));
      
      // Update in local users array
      setUsers(prev => {
        return prev.map(u => u.id === user.id ? { ...u, ...updatedUserData } : u);
      });
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  const addUser = async (newUserData) => {
    try {
      // Create user in Firebase
      const newUser = await createUser(newUserData);
      
      // Update local users state
      setUsers(prev => [...prev, newUser]);
      
      return newUser;
    } catch (error) {
      console.error('Add user error:', error);
      throw error;
    }
  };

  const removeUser = async (userId) => {
    if (userId === user?.id) {
      throw new Error('Cannot remove your own account');
    }
    
    try {
      // Delete user from Firebase
      await deleteUser(userId);
      
      // Update local users state
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Remove user error:', error);
      throw error;
    }
  };

  const getAllUsers = () => {
    return users;
  };

  const refreshUsers = async () => {
    try {
      const { getAllUsers: getUsers } = await import('../services/userService');
      const firebaseUsers = await getUsers();
      setUsers(firebaseUsers);
      return firebaseUsers;
    } catch (error) {
      console.error('Refresh users error:', error);
      throw error;
    }
  };

  const updateUserByAdmin = async (userId, updatedUserData) => {
    try {
      // Update user in Firebase
      await updateUserInFirebase(userId, updatedUserData);
      
      // Update local users array
      setUsers(prev => {
        return prev.map(u => u.id === userId ? { ...u, ...updatedUserData } : u);
      });
      
      // If this is the current user, update their session data
      if (user && user.id === userId) {
        const updatedUser = { ...user, ...updatedUserData };
        setUser(updatedUser);
        localStorage.setItem('bullbearly-user', JSON.stringify(updatedUser));
      }
      
      return { id: userId, ...updatedUserData };
    } catch (error) {
      console.error('Update user by admin error:', error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    signup,
    logout,
    updateUser,
    updateUserByAdmin,
    addUser,
    removeUser,
    getAllUsers,
    refreshUsers,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
